import { BASE_ORIENTATION } from '../constant';
import { new__cn as cn, createTwStyle } from '../util';
import type { VBaseInputContextValue, VBaseInputProps } from './type';
import { BASE_INPUT_SIZE } from './variable';

const getBorderColor = ({
  status,
}: Pick<VBaseInputContextValue, 'status'>): string => {
  if (status.disabled) {
    return 'border-gray-200 bg-gray-200';
  }

  if (status.valid === false && !status.readOnly) {
    return 'border-error-500';
  }

  if (status.focused && !status.readOnly) {
    return 'border-secondary-300';
  }

  if (status.readOnly) {
    return 'border-gray-200';
  }

  return 'border-gray-300';
};

const getRounded = ({ size }: VBaseInputProps) => {
  switch (size) {
    case BASE_INPUT_SIZE.XL:
    case BASE_INPUT_SIZE.LG:
      return 'rounded-md';
    case BASE_INPUT_SIZE.MD:
    case BASE_INPUT_SIZE.SM:
      return 'rounded-sm';
    default:
      return 'rounded-md';
  }
};

export const getGap = ({ orientation, size }: VBaseInputProps) => {
  if (orientation === BASE_ORIENTATION.HORIZONTAL) {
    switch (size) {
      case BASE_INPUT_SIZE.XL:
      case BASE_INPUT_SIZE.LG:
      case BASE_INPUT_SIZE.MD:
      case BASE_INPUT_SIZE.SM:
        return 4;
      default:
        return 4;
    }
  }

  switch (size) {
    case BASE_INPUT_SIZE.XL:
    case BASE_INPUT_SIZE.LG:
      return 4;
    case BASE_INPUT_SIZE.MD:
    case BASE_INPUT_SIZE.SM:
      return 2;
    default:
      return 4;
  }
};

const getPadding = ({ orientation, size }: VBaseInputProps) => {
  if (orientation === BASE_ORIENTATION.HORIZONTAL) {
    switch (size) {
      case BASE_INPUT_SIZE.XL:
        return 'py-[3px] px-[7px]';
      case BASE_INPUT_SIZE.LG:
        return 'py-[7px] px-[7px]';
      case BASE_INPUT_SIZE.MD:
      case BASE_INPUT_SIZE.SM:
        return 'py-[3px] px-[7px]';
      default:
        return 'py-[7px] px-[7px]';
    }
  }

  switch (size) {
    case BASE_INPUT_SIZE.XL:
      return 'py-[3px] px-[7px]';
    case BASE_INPUT_SIZE.LG:
      return 'py-[7px] px-[7px]';
    case BASE_INPUT_SIZE.MD:
    case BASE_INPUT_SIZE.SM:
      return 'py-[3px] px-[7px]';
    default:
      return 'py-[7px] px-[7px]';
  }
};

const twStyle = createTwStyle<
  VBaseInputProps & Pick<VBaseInputContextValue, 'status'>
>();

export default twStyle({
  container: ({ fluid }) => [cn({ 'w-full': fluid })],

  comment: ({ size, status }) => [
    cn(
      'px-2 select-none',
      {
        'text-gray-600': status.valid !== false,
        'text-error-500': status.valid === false,
      },
      {
        'text-body-sm': size === BASE_INPUT_SIZE.SM,
        'text-body-md': size !== BASE_INPUT_SIZE.SM,
      },
    ),
  ],

  textboxWrapper: ({
    orientation = BASE_ORIENTATION.VERTICAL,
    size = BASE_INPUT_SIZE.LG,
    status,
  }) => [
    cn(
      'relative',
      'border',
      'bg-white',
      !status.disabled && !status.readOnly && 'hover:shadow-sm',
      'overflow-hidden',
      getBorderColor({ status }),
      getRounded({ size }),
      {
        'flex flex-col': orientation === BASE_ORIENTATION.VERTICAL,
        'flex items-center': orientation === BASE_ORIENTATION.HORIZONTAL,
      },
      getPadding({ orientation, size }),
    ),
  ],
  labelWrapper: ({ orientation, size }) => [
    cn(
      {
        'max-w-full min-w-0 truncate':
          orientation === BASE_ORIENTATION.HORIZONTAL,
      },
      {
        'text-body-sm': size === BASE_INPUT_SIZE.SM,
        'text-body-md': size !== BASE_INPUT_SIZE.SM,
      },
    ),
  ],
});
